<template>
  <TreeSearch
    ref="treeSearch"
    :items="props.items"
    :showExpanded="props.showExpanded"
    :expandOnSearch="true"
    :inputPlaceholder="searchPlaceholder ?? 'Поиск по кодам'"
  >
    <template #item="{ item }">
      <CodesTreeItem
        :item="item"
        :selectState="selectState"
        :selectMode="props.selectMode"
        :disabled="disabled"
        @change="onChangeSelection"
      />
    </template>
  </TreeSearch>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue'
import { cloneDeep } from 'lodash-es'
import TreeSearch from '@/components/basic/Tree/TreeSearch.vue'
import CodesTreeItem from '@/ContextTab/modules/news/codes/components/codesTree/CodesTreeItem.vue'
import { EQuadboxState } from '@/ContextTab/components/UI/types'
import type {
  CodeSelectMode,
  CodeSelectState,
  CodeTreeSelectEvent,
} from '@/ContextTab/modules/news/codes/types.ts'
import type { CodeWithCategoryAndChildren } from '@/api/generated/v2'


const props = defineProps<{
  items: CodeWithCategoryAndChildren[]
  included: number[]
  excluded?: number[]
  disabled?: number[]
  selectMode: CodeSelectMode
  showExpanded?: boolean
  searchPlaceholder?: string
}>()

const emit = defineEmits<{
  (e: 'change', payload: CodeSelectState): void
}>()

const selectState = ref<CodeSelectState>({})

const treeSearch = useTemplateRef<InstanceType<typeof TreeSearch>>('treeSearch')

watch(
  () => [...props.included, ...props.excluded ?? []],
  () => {
    // force rewrite
    selectState.value = {}
    props.included.forEach((id) => selectState.value[id] = EQuadboxState.included)
    props.excluded?.forEach((id) => selectState.value[id] = EQuadboxState.excluded)
    recalcImpliedSelection()
  },
  { immediate: true },
)

watch(
  () => props.items,
  () => setTimeout(() => treeSearch.value?.scrollToTop(), 0),
)

function isExplicitState(state: EQuadboxState): boolean {
  return state === EQuadboxState.included || state === EQuadboxState.excluded
}

function implicitFrom(state: EQuadboxState): EQuadboxState {
  return state === EQuadboxState.included ? EQuadboxState.partial : EQuadboxState.partialExcluded
}

function recalcImpliedSelection() {

  selectState.value = Object.fromEntries(
    Object.entries(selectState.value)
      .filter(([_, state]) => isExplicitState(state)),
  )

  function processCodeItem(item: CodeWithCategoryAndChildren, inheritState: EQuadboxState | null) {
    const explicitState = selectState.value[item.id]

    if (explicitState && isExplicitState(explicitState)) {
      inheritState = implicitFrom(explicitState)
    } else if (inheritState) {
      selectState.value[item.id] = inheritState
    }

    item.children?.forEach((item) => processCodeItem(item, inheritState))
  }

  props.items.forEach((item) => processCodeItem(item, null))
}

function onChangeSelection(event: CodeTreeSelectEvent) {
  Object.assign(selectState.value, { [event.id]: event.state })

  recalcImpliedSelection()
  emit('change', cloneDeep(selectState.value))
}

function uncheckItem(id: number) {
  delete selectState.value[id]

  recalcImpliedSelection()

  emit('change', cloneDeep(selectState.value))
}

defineExpose({
  uncheckItem,
})
</script>
